// Naming the controls on a page so Kit can see them.
//
// Kit's `request_screenshot` tool sounds like it takes a picture. It does not:
// screenshot_capture_controller.js walks the DOM and uploads a markdown
// transcript, and that text is the entire world Kit gets to reason about. A
// control this module cannot name is a control Kit does not know exists — and
// Kit, asked "why isn't the button there?", will answer that it isn't.
//
// The old capture named a button by `textContent || value` and dropped it
// outright when both were empty. Every icon-only affordance in this app is
// exactly that shape:
//
//   = button_to saved_influencers_path, method: :post do
//       %i.icon.heart.outline.large
//
// so hearts, kebab menus, refresh arrows and trash cans were all invisible.
// Two customer conversations turned on it (2026-07-16, 2026-08-25), both ending
// in Kit confidently describing a present control as missing.
//
// The rule here: fall back through the identities a control can carry —
// aria-label, title, alt, then the icon glyph itself — and only give up when
// there is genuinely nothing to say.

// Fomantic writes an icon as `<i class="heart outline large icon">`: the icon's
// name is mixed into the same class list as sizing, colour and layout
// modifiers. Stripping the modifiers leaves the part a reader cares about.
//
// `outline` is deliberately NOT a modifier. "heart" and "heart outline" are
// different states of the same control — saved vs. not saved — and collapsing
// them would answer "is this creator already saved?" wrongly.
const ICON_MODIFIERS = new Set([
  "icon",
  "icons",
  "ui",
  "link",
  "fitted",
  "circular",
  "bordered",
  "inverted",
  "loading",
  "disabled",
  "flipped",
  "rotated",
  "corner",
  "colored",
  "mini",
  "tiny",
  "small",
  "medium",
  "large",
  "big",
  "huge",
  "massive",
  "horizontally",
  "vertically",
  "clockwise",
  "counterclockwise",
  "left",
  "right",
  "top",
  "bottom",
  "black",
  "grey",
  "gray",
  "white",
  "red",
  "orange",
  "yellow",
  "olive",
  "green",
  "teal",
  "blue",
  "violet",
  "purple",
  "pink",
  "brown",
]);

// Only an actual `<i>` names a glyph.
//
// Fomantic puts `icon` on the CONTROL as a layout class —
// `<button class="ui icon button">`, `<a class="ui icon fluid button">` — so
// reading a control's own class list describes the control, not what is drawn
// in it. Browser QA on the real Vetting page caught this shipping as
// `[Button: button secondary icon]`, which is worse than the omission it
// replaced: it tells Kit there is a control called "button secondary".
//
// `tagName` is passed rather than sniffed so this stays a pure function.
export function iconNamesForControl({tagName, className, descendantIconClassName} = {}) {
  if (String(tagName || "").toLowerCase() === "i") {
    return iconNamesFromClassList(className);
  }

  return iconNamesFromClassList(descendantIconClassName);
}

// `vertical` is a Fomantic modifier in most contexts but it is also the whole
// difference between the horizontal and vertical ellipsis (the kebab menu), so
// it is kept when it qualifies an icon name rather than standing alone.
export function iconNamesFromClassList(classList) {
  const classes = String(classList || "")
    .split(/\s+/)
    .filter(Boolean);

  if (!classes.includes("icon") && !classes.includes("icons")) return [];

  return classes.filter((name) => !ICON_MODIFIERS.has(name));
}

// The name to print for a control, in descending order of how much the author
// told us. Returns null when the control carries no identity — a genuinely
// anonymous element, which is the only case where dropping it loses nothing.
export function controlLabel({ text, ariaLabel, title, alt, iconNames } = {}) {
  const trimmed = String(text || "").trim();
  if (trimmed) return trimmed;

  const explicit = [ariaLabel, title, alt]
    .map((value) => String(value || "").trim())
    .find(Boolean);
  if (explicit) return explicit;

  const icons = (iconNames || []).filter(Boolean);
  if (icons.length) return `${icons.join(" ")} icon`;

  return null;
}
